import React, { useEffect, useState, useRef } from 'react'
import {
  Button,
  Flex,
  MainHeadline,
  ProfilePicture,
  Table,
  Th,
  Tr,
  PageWrapper,
  PageFooter,
  CursorPointer,
  ColoredDot,
  darkTheme,
} from '../../styling/GlobalStyles'
import { useNavigate } from 'react-router-dom'
import { Pagination } from '../../components/Pagination/Pagination'
import Avatar from '../../assets/images/profileAvatar.png'
import { Rating } from './Rating'
import { NavBar } from '../../components/NavBar/NavBar'
import { useDispatch, useSelector } from 'react-redux'
import {
  addUser,
  clearEmployeesList,
  deleteUser,
  getUsersSimpleInfo,
  getUserStats,
} from 'features/users(Employees)/usersSlice'
import { AppDispatch, RootState } from 'store'
import { Options } from 'components/Options/Options'
import { NoItems } from 'components/NoItems-fallback/NoItems'
import { handlePagination, getParams } from 'utils/helpers'
import { Td } from 'components/Td/Td'
import { setConfirmModal } from 'features/global/globalSlice'
import useAuth from 'hooks/useAuth'
import { ModalTypes } from 'types/global.interface'
import { Modal } from 'components/Modal/Modal'
import { CloseIcon } from 'assets/icons/CloseIcon'
import { ImageModalFrame } from './Employees.styled'
import { FilterEnum, LinkEnum } from 'components/Filter/SingleFilter'
import useQueryPagination from 'hooks/useQueryPagination'
import { contractTypes } from './employeesData'
import { ImportEmployeesModal } from './ImportEmployeesModal'
import { BodyText, OtherText } from 'styling/Typography'
import { attendanceTypesOptions } from 'pages/Attendance/data'
import { TerminateContractModal } from './TerminateContractModal'

export const Employees = () => {
  const { users, totalPages } = useSelector((store: RootState) => store.users)
  const dispatch = useDispatch<AppDispatch>()
  const navigate = useNavigate()
  const { user } = useAuth()
  const [params, setParams] = useState<any>(getParams({ location }))
  const [imageModal, setImageModal] = useState({ isOpen: false, image: null })
  const [importModal, setImportModal] = useState(false)
  const [terminateContractModal, setTerminateContractModal] = useState({ open: false, id: null })

  useQueryPagination({
    fetchFunctions: [getUsersSimpleInfo, getUserStats],
    cleanUpFunctions: [clearEmployeesList],
    params,
  })

  const filters = [
    {
      type: FilterEnum.FIXED_LIST,
      name: 'State',
      state: null,
      propName: 'state',
      sd: true,
      single: true,
      filterOptions: [
        {
          name: 'Active',
          id: '1',
        },
        {
          name: 'Contract Terminated',
          id: '3',
        },
      ],
    },
    {
      type: FilterEnum.FIXED_LIST,
      name: 'Employee Type',
      state: null,
      propName: 'filterType',
      sd: true,
      single: true,
      filterOptions: [
        {
          name: 'Company',
          id: '1',
        },
        {
          name: 'Contractors',
          id: '2',
        },
      ],
    },
    {
      type: FilterEnum.SEARCH,
      link: LinkEnum.ROLES,
      name: 'Roles',
      state: null,
    },
  ]

  const handleDelete = (id: number | string) => {
    dispatch(
      setConfirmModal({
        isOpen: true,
        onSave: async () => {
          await dispatch(deleteUser(id))
          dispatch(getUserStats())
        },
        setParams: {
          type: ModalTypes.Delete,
          f: setParams,
          noItems: users.length < 2,
        },
        word: 'delete employee',
      }),
    )
  }

  const ImageModal = () => {
    const imageModalRef = useRef(null)

    useEffect(() => {
      const outSideClick = (e) => {
        if (!imageModalRef.current.contains(e.target)) {
          setImageModal({ isOpen: false, image: null })
        }
      }

      document.addEventListener('mousedown', outSideClick)

      return () => {
        document.removeEventListener('mousedown', outSideClick)
      }
    }, [])

    return (
      <Flex column gap='2rem' ref={imageModalRef}>
        <Flex end='true' width='100%'>
          <CursorPointer onClick={() => setImageModal({ isOpen: false, image: null })}>
            <CloseIcon />
          </CursorPointer>
        </Flex>

        <ImageModalFrame>
          <img
            src={imageModal.image || Avatar}
            alt='profile-image'
            onError={({ currentTarget }) => (currentTarget.src = Avatar)}
          />
        </ImageModalFrame>
      </Flex>
    )
  }

  return (
    <>
      <NavBar
        enableTemporaryManualClosing={true}
        filterOptions={filters}
        params={params}
        defaultValue={params.q}
        input
        placeholder='Search Employees...'
        setParams={setParams}
        limit={30}
      />
      <PageWrapper noFooter={totalPages < 2}>
        <Flex between center mb='16'>
          <MainHeadline>Employees</MainHeadline>
          <Flex gap='1rem'>
            <Button orange onClick={() => setImportModal(true)}>
              Import Employees
            </Button>
            <Button orange onClick={() => navigate('/hr/employees/add')}>
              Add Employee
            </Button>
          </Flex>
        </Flex>
        {!users?.length ? (
          <NoItems title='Employees' />
        ) : (
          <Table>
            <Th next>
              <Td w='6%' noTooltip text='ID' />
              <Td w='5%' noTooltip text='' />
              <Td w='15%' noTooltip text='Full Name' />
              <Td w='15%' noTooltip text='Phone' />
              <Td w='15%' noTooltip text='Net Salary' />
              <Td w='15%' noTooltip text='Accommodation' />
              <Td w='13%' noTooltip text='Rating' />
              <Td w='13%' noTooltip text='Status' />
              <Td w='5%' noTooltip text='' />
            </Th>
            {users &&
              users.map((item) => {
                const attendanceType = attendanceTypesOptions.find(
                  (a) => a.value == item.attendanceType,
                )
                return (
                  <Tr
                    view
                    style={{ cursor: 'pointer' }}
                    key={item.id}
                    onClick={() =>
                      navigate('/dashboard/employees/' + item.id + '?fromEmployees=true')
                    }
                  >
                    <Td w='6%'>
                      <BodyText>{item.accessCardId || '/'}</BodyText>
                    </Td>
                    <Td w='5%' noTooltip>
                      <Flex center>
                        <ProfilePicture>
                          <img
                            src={item.getImagePresignedUrl || Avatar}
                            alt=''
                            onClick={() =>
                              setImageModal({ isOpen: true, image: item.getImagePresignedUrl })
                            }
                            onError={({ currentTarget }) => {
                              currentTarget.onerror = null
                              currentTarget.src = Avatar
                            }}
                          />
                        </ProfilePicture>
                      </Flex>
                    </Td>
                    <Td w='15%'>
                      <Flex column style={{ maxWidth: '100%', overflow: 'hidden' }}>
                        <BodyText
                          weight='bold'
                          style={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            maxWidth: '100%',
                          }}
                        >
                          {item?.fullName}
                        </BodyText>
                        <OtherText>{item?.role}</OtherText>
                      </Flex>
                    </Td>
                    <Td w='15%'>
                      <Flex column>
                        <BodyText>{item?.phoneNumber}</BodyText>
                        {item?.secondaryPhoneNumber && (
                          <BodyText>{item?.secondaryPhoneNumber}</BodyText>
                        )}
                      </Flex>
                    </Td>
                    <Td w='15%'>
                      <BodyText>
                        {item.contract?.value
                          ? 'GH₵ ' +
                          item.contract?.value +
                          ' ' +
                          contractTypes.find((c) => c.value == item.contract?.type).label
                          : 'No active contracts'}
                      </BodyText>
                    </Td>
                    <Td w='15%'>
                      <BodyText>{item?.accommodation?.name || 'No Accommodation'}</BodyText>
                    </Td>
                    <Td w='13%' noTooltip>
                      <Rating rating={item.overallRating?.toFixed(1)} />
                    </Td>
                    <Td w='13%' noTooltip>
                      <Flex gap='1rem' center>
                        {attendanceType && (
                          <ColoredDot
                            color={
                              attendanceType.color ? darkTheme[attendanceType.color] : '#ff9900'
                            }
                          />
                        )}
                        {/* TODO: Ubaciti gore i za light temu */}
                        {attendanceType?.label || 'Not Set'}
                      </Flex>
                    </Td>
                    <Td
                      w='5%'
                      options
                      noTooltip
                      onClick={(e) => {
                        e.stopPropagation()
                        return null
                      }}
                    >
                      <Options
                        item={item}
                        invisible={user.id == item.id}
                        title='Employee'
                        handleRemove={() => handleDelete(item.id)}
                        openModal={(id) => setTerminateContractModal({ open: true, id })}
                      />
                    </Td>
                  </Tr>
                )
              })}
          </Table>
        )}
        {imageModal.isOpen && (
          <Modal black>
            <ImageModal />
          </Modal>
        )}
        {importModal && <ImportEmployeesModal closeModal={() => setImportModal(false)} />}
        {terminateContractModal.open && (
          <TerminateContractModal
            id={terminateContractModal.id}
            closeModal={() => setTerminateContractModal({ open: false, id: null })}
          />
        )}
      </PageWrapper>
      <PageFooter>
        <Pagination
          setPage={(page) => handlePagination(page, setParams, 30)}
          pageCount={Math.ceil(totalPages)}
          forcePage={(params.page ? params.page : 1) - 1}
        />
      </PageFooter>
    </>
  )
}
